import {
  IconCircleCheck,
  IconCircleX,
  IconEdit,
  IconEye,
  IconTrash,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { Anchor, Text } from "@mantine/core";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import {
  canEditSalesOrderItems,
  PRINT_STATUS_COLOR,
  PRINT_STATUS_LABEL,
  SALES_ORDER_EDIT_MODE,
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import { salesOrderApi } from "apis/salesOrderApi";
import { formatCurrency } from "utils/helpers/format";
import {
  SaleOrderParamsType,
  SalesOrderResponseType,
} from "constants/types/salesOrder";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FC, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSalesOrders } from "utils/hooks/request/useSalesOrder";
import { Loading } from "components";
import { SALES_ORDER_DETAIL_PAGE } from "routes/route.constant";
import { useNotification } from "utils/hooks";
import { isDisableActions } from "./config";

interface SalesOrderTableProps {
  params: SaleOrderParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const SalesOrderTable: FC<SalesOrderTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { notification } = useNotification();
  const { salesOrders, pagination, isLoading, mutate } = useSalesOrders(params);
  const [isActing, setIsActing] = useState(false);

  const handleNavigateToDetail = useCallback(
    (id: string) => {
      navigate(SALES_ORDER_DETAIL_PAGE.replace(":id", id));
    },
    [navigate],
  );

  const handleConfirm = useCallback(
    (row: SalesOrderResponseType) => {
      modals.openConfirmModal({
        title: "Xác nhận đơn bán hàng",
        children: (
          <Text size="sm">
            Bạn có chắc chắn muốn xác nhận đơn bán hàng{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không? Hành động này không thể hoàn tác.
          </Text>
        ),
        labels: { confirm: "Xác nhận", cancel: "Hủy" },
        confirmProps: { color: "green" },
        onConfirm: async () => {
          setIsActing(true);
          try {
            await salesOrderApi.confirm(row.id);
            notification.success({ title: "Xác nhận đơn bán hàng thành công" });
            mutate();
          } catch (error: any) {
            notification.error({
              title: "Xác nhận thất bại",
              message: error?.error?.message,
            });
          }
          setIsActing(false);
        },
      });
    },
    [mutate, notification],
  );

  const handleEdit = useCallback(
    (row: SalesOrderResponseType) => {
      modals.openConfirmModal({
        title: "Chỉnh sửa đơn bán hàng",
        children: (
          <Text size="sm">
            Bạn có chắc muốn chỉnh sửa đơn bán hàng{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không?
          </Text>
        ),
        labels: { confirm: "Chỉnh sửa", cancel: "Hủy" },
        confirmProps: { color: "blue" },
        onConfirm: () =>
          navigate(
            SALES_ORDER_DETAIL_PAGE.replace(":id", row.id) +
              `?mode=${SALES_ORDER_EDIT_MODE}`,
          ),
      });
    },
    [navigate],
  );

  const handleCancel = useCallback(
    (row: SalesOrderResponseType) => {
      modals.openConfirmModal({
        title: "Hủy xác nhận đơn bán hàng",
        children: (
          <Text size="sm">
            Bạn có chắc chắn muốn hủy xác nhận đơn bán hàng{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không? Hành động này không thể hoàn tác.
          </Text>
        ),
        labels: { confirm: "Hủy xác nhận", cancel: "Đóng" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          setIsActing(true);
          try {
            await salesOrderApi.cancel(row.id);
            notification.success({
              title: "Hủy xác nhận đơn bán hàng thành công",
            });
            mutate();
          } catch (error: any) {
            notification.error({
              title: "Hủy xác nhận thất bại",
              message: error?.error?.message,
            });
          }
          setIsActing(false);
        },
      });
    },
    [mutate, notification],
  );

  const handleDelete = useCallback(
    (row: SalesOrderResponseType) => {
      modals.openConfirmModal({
        title: "Xóa đơn bán hàng",
        children: (
          <Text size="sm">
            Bạn có chắc chắn muốn xóa đơn bán hàng{" "}
            <Text component="span" fw={600}>
              {row.code}
            </Text>{" "}
            không?
          </Text>
        ),
        labels: { confirm: "Xóa", cancel: "Đóng" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          setIsActing(true);
          try {
            await salesOrderApi.delete(row.id);
            notification.success({ title: "Xóa đơn bán hàng thành công" });
            mutate();
          } catch (error: any) {
            notification.error({
              title: "Xóa đơn bán hàng thất bại",
              message: error?.error?.message,
            });
          }
          setIsActing(false);
        },
      });
    },
    [mutate, notification],
  );

  const rowActions: ActionItem<SalesOrderResponseType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "sale_order:read",
        onClick: (row) => handleNavigateToDetail(row.id),
      },
      {
        key: "confirm",
        label: "Xác nhận đơn",
        icon: <IconCircleCheck size={ICON_SIZE} />,
        color: "green",
        permission: "sale_order:confirm",
        disabled: (row) => isDisableActions(row.status),
        onClick: handleConfirm,
      },
      {
        key: "cancel",
        label: "Hủy đơn",
        icon: <IconCircleX size={ICON_SIZE} />,
        color: "red",
        permission: "sale_order:cancel",
        disabled: (row) => isDisableActions(row.status),
        onClick: handleCancel,
      },
      {
        key: SALES_ORDER_EDIT_MODE,
        label: "Chỉnh sửa đơn",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "blue",
        permission: "sale_order:update",
        hidden: (row) => !canEditSalesOrderItems(row.status, row.totalAmount),
        onClick: handleEdit,
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "sale_order:delete",
        onClick: handleDelete,
      },
    ],
    [
      handleConfirm,
      handleCancel,
      handleNavigateToDetail,
      handleEdit,
      handleDelete,
    ],
  );

  const hasActions = useMemo(
    () =>
      rowActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [rowActions, ability],
  );

  const columns: MRT_ColumnDef<SalesOrderResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã đơn hàng",
      size: 170,
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
      Cell: ({ row }) => (
        <Anchor
          size="sm"
          onClick={() => handleNavigateToDetail(row.original.id)}
        >
          {row.original.code}
        </Anchor>
      ),
    },
    {
      accessorKey: "partner.name",
      header: "Khách hàng",
      minSize: 260,
      grow: true,
      mantineTableHeadCellProps: { align: "center" },
    },
    {
      accessorKey: "warehouse.name",
      header: "Kho xuất",
      size: 150,
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
    },
    {
      accessorKey: "totalAmount",
      header: "Tổng tiền",
      size: 130,
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "right" },
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
    },
    // {
    //   accessorKey: "oldDebt",
    //   header: "Công nợ cũ",
    //   Cell: ({ cell }) => {
    //     const value = cell.getValue<number | null | undefined>();
    //     return value && value > 0 ? (
    //       <Text size="sm" fw={600} c="orange.7">
    //         {formatCurrency(value)}
    //       </Text>
    //     ) : (
    //       <Text size="sm" c="dimmed">—</Text>
    //     );
    //   },
    // },
    {
      accessorKey: "paidAmount",
      header: "Đã thanh toán",
      size: 150,
      grow: false,
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "right" },
    },
    {
      accessorKey: "status",
      header: "Trạng thái đơn hàng",
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const status = cell.getValue<string>();
        return (
          <CustomBadge
            label={SALES_ORDER_STATUS_LABEL[status] ?? status}
            color={SALES_ORDER_STATUS_COLOR[status] ?? "gray"}
          />
        );
      },
    },
    {
      accessorKey: "isPrinted",
      header: "Trạng thái in",
      grow: false,
      size: 110,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const key = String(cell.getValue<boolean>());
        return (
          <CustomBadge
            label={PRINT_STATUS_LABEL[key]}
            color={PRINT_STATUS_COLOR[key]}
          />
        );
      },
    },
    {
      // Người phụ trách của ĐƠN nếu có, không thì của khách hàng. Không phải
      // người bấm tạo đơn.
      id: "contactPerson",
      header: "Người phụ trách",
      size: 170,
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
      Cell: ({ row }) =>
        row.original.contactPerson?.fullname ??
        row.original.partner?.contactPerson?.fullname ??
        "—",
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            size: 140,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={rowActions} />
            ),
          } satisfies MRT_ColumnDef<SalesOrderResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading || isActing}>
      <CustomTable
        columns={columns}
        data={salesOrders ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
        mrtOptions={{
          enableColumnFilters: false,
          initialState: { density: "xs" },
        }}
      />
    </Loading>
  );
};

export default SalesOrderTable;
